import { Fragment, useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import { IconButton } from '@pocketlaw/tetris';
import { Link } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Falcon } from 'modules/macro';
import { FloatingSidebar } from 'shared/domains/common-ui/ScreenLayout';
import { DocumentPreviewContent } from './DocumentPreviewContent';
import { useDocumentPreview } from './useDocumentPreview';
import { useDocumentPreviewData } from './useDocumentPreviewData';
import { useDocumentPreviewKeyboardNavigation } from './useDocumentPreviewKeyboardNavigation';
const NAVIGATION_DEBOUNCE_MS = 300;
export function DocumentPreviewSidebar() {
    const { documentId, previousDocumentId, nextDocumentId, previewDocument, closePreview } = useDocumentPreview();
    /**
     * The sidebar stays mounted so that it can animate when it closes. We keep rendering the last
     * previewed document until another one is opened, so the content does not disappear mid animation.
     */
    const [previewedId, setPreviewedId] = useState(documentId);
    /**
     * Opening the preview shows the document right away (leading call), but quickly stepping through
     * documents only fetches the one the user settles on (trailing call), instead of firing and
     * cancelling a request for every document passed along the way.
     */
    const updatePreviewedId = useDebouncedCallback(setPreviewedId, NAVIGATION_DEBOUNCE_MS, {
        leading: true,
        trailing: true,
    });
    const { document, name, html, renderType, loading, error } = useDocumentPreviewData(previewedId);
    const isNavigating = Boolean(documentId) && documentId !== previewedId;
    const fallbackTitle = t({
        message: 'Preview',
        comment: 'Document preview sidebar - Title',
    });
    const goToPrevious = () => {
        if (previousDocumentId) {
            previewDocument(previousDocumentId);
        }
    };
    const goToNext = () => {
        if (nextDocumentId) {
            previewDocument(nextDocumentId);
        }
    };
    useDocumentPreviewKeyboardNavigation({
        enabled: Boolean(documentId),
        onPrevious: goToPrevious,
        onNext: goToNext,
    });
    useEffect(() => {
        if (documentId) {
            updatePreviewedId(documentId);
        }
        else {
            updatePreviewedId.cancel();
        }
    }, [documentId, updatePreviewedId]);
    const hasNavigation = Boolean(previousDocumentId || nextDocumentId);
    return (<FloatingSidebar.Root open={Boolean(documentId)} initialWidth="wide">
      <FloatingSidebar.Header title={name || fallbackTitle} onClose={closePreview} actions={<Fragment>
            {hasNavigation && (<Fragment>
                <IconButton appearance="ghost" size="xs" aria-label={t({
                    message: 'Previous document',
                    comment: 'Document preview sidebar - Previous document button label',
                })} disabled={!previousDocumentId} onClick={goToPrevious}>
                  <Falcon icon="chevron-up"/>
                </IconButton>
                <IconButton appearance="ghost" size="xs" aria-label={t({
                    message: 'Next document',
                    comment: 'Document preview sidebar - Next document button label',
                })} disabled={!nextDocumentId} onClick={goToNext}>
                  <Falcon icon="chevron-down"/>
                </IconButton>
              </Fragment>)}
            <IconButton as={Link} to={`/document/${documentId}`} appearance="ghost" size="xs" aria-label={t({ comment: 'Open document tooltip', message: 'Open document' })}>
              <Falcon icon="eye"/>
            </IconButton>
          </Fragment>}/>
      <FloatingSidebar.Body scrollable={renderType === 'html'}>
        <DocumentPreviewContent document={document} html={html} renderAsPdf={renderType === 'pdf'} loading={loading || isNavigating} error={Boolean(error)}/>
      </FloatingSidebar.Body>
    </FloatingSidebar.Root>);
}
